import React, { useCallback, useEffect, useRef, useState } from 'react';
import L, { LatLngExpression, LeafletMouseEvent } from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { Feature, featureCollection, feature, Geometry } from '@turf/turf';
import { AppDispatch, RootState } from '../../store/Store';
import {
	baseStyle,
	hybridLayerParams,
	maxBoundsContinentalUSParams,
	streetsLayerParams,
	satelliteLayerParams,
	selectedOpacity,
	unselectedOpacity,
} from '../Shared/MapCommon';
import { get } from 'lodash';
import { ZoomTray } from '../ZoomTray';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
/**
 * In case a map marker is ever needed, this is a workaround, otherwise it will fail to get
 * the marker icon file properly.
 * Ref: https://github.com/PaulLeCam/react-leaflet/issues/453
 */
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { useTheme } from 'styled-components';
import { connect, ConnectedProps } from 'react-redux';
import { addSingleSelectedField, setShowMapSearch } from '../../store/UI/UISlice';

const DefaultIcon = L.icon({
	iconUrl: icon,
	shadowUrl: iconShadow
});

L.Marker.prototype.options.icon = DefaultIcon;
/**
 * end map marker solution
 */

export interface IJdConnectFarmFieldOrgResponse
{
	Id: string;
	Name: string;
	Key: string;
	FarmList: IJdConnectFarm[];
}

export interface IJdConnectFarm
{
	Id: string;
	Name: string;
	Key: string;
	Fields: IJdConnectField[];

	OrgKey: string;
	Color: string;
}

export interface IJdConnectField
{
	Id: string;
	Name: string;
	Key: string;
	Area: number;
	BoundaryId: string;
	Authorized: boolean;
	Geometry: Geometry;

	FarmKey: string;
	Color: string;
}

interface IJdConnectFieldMapProps extends PropsFromRedux
{
	currentSelectedFieldIds: string[];
	orgFarmFieldList: IJdConnectFarmFieldOrgResponse[];
	selectSingleField: (fieldId: string) => void;
}

const MapComponent = (props: IJdConnectFieldMapProps) =>
{
	const {
		currentSelectedFieldIds,
		orgFarmFieldList,
		selectSingleField,
		DisplayMapSearch,
		MapboxToken,
		SetShowMapSearch,
	} = props;

	const [fields, setFields] = useState<IJdConnectField[]>([]);
	
	const theme = useTheme();

	// Reference to the map control
	const mapRef = useRef<L.DrawMap | undefined>(undefined);

	// TODO: For future popups: Reference to the div of any popup
	const [popupRef, setPopupRef] = useState<HTMLDivElement | undefined>(undefined);

	// Store the element the map will be rendered into
	const [mapContainer, setMapContainer] = useState<HTMLDivElement>();

	// Store the geojson data layer we will use to represent everything.
	const [geoJsonLayer] = useState(L.geoJSON());

	// Base Layers for the map
	const hybridLayer = L.tileLayer((hybridLayerParams[0] as string).replace('MAPBOXTOKEN', MapboxToken), hybridLayerParams[1] as {attribution: string});
	const streetsLayer = L.tileLayer((streetsLayerParams[0] as string).replace('MAPBOXTOKEN', MapboxToken), streetsLayerParams[1] as {attribution: string});
	const satelliteLayer = L.tileLayer((satelliteLayerParams[0] as string).replace('MAPBOXTOKEN', MapboxToken), satelliteLayerParams[1] as {attribution: string});

	// Create a layer object to pass to the layer control on the map
	const baseMaps = {
		'Hybrid': hybridLayer,
		'Streets': streetsLayer,
		'Satellite': satelliteLayer
	};

	// Continental US bounds
	const maxBoundsContinentalUS: L.LatLngBounds =  L.latLngBounds(
		L.latLng(maxBoundsContinentalUSParams[0][0], maxBoundsContinentalUSParams[0][1]), //Southwest
		L.latLng(maxBoundsContinentalUSParams[1][0], maxBoundsContinentalUSParams[1][1])  //Northeast
	);

	// Create the default map styler that will apply color to the rendered vectors.
	const styler = ((feature: Feature<Geometry, IJdConnectField & { isDrawn: Boolean }>) =>
	{
		if (feature.properties && currentSelectedFieldIds && currentSelectedFieldIds.find((sf: string) => sf === feature.properties?.Id))
		{
			return {
				...baseStyle(theme),
				fillColor: theme.colors.primary,
				fillOpacity: selectedOpacity
			};
		}
		else
		{
			const foundField = fields.find(f => f.Id === feature.properties?.Id);
			if (foundField)
			{
				return {
					...baseStyle(theme),
					fillColor: foundField.Color,
					fillOpacity: unselectedOpacity,
				};
			}			
		}
		return { ...baseStyle(theme) };

	}) as L.StyleFunction<any>;

	geoJsonLayer.setStyle(styler);

	// When the container is rendered, save the element so we can install the map there
	const installMap = useCallback((ref: HTMLDivElement) =>
	{
		if (ref)
		{
			// On refreshes, if the mapRef exists, it can throw an error about the map already having been initialized
			// Removing it before we re-initialize it will fix that
			if (mapRef.current)
			{
				mapRef.current.remove();
			}

			mapRef.current = L.map(ref, {
				zoomControl: false,
				//zoom: defaultZoomLevel,
				layers: [hybridLayer] // default to the Streets layer
			}).fitBounds(maxBoundsContinentalUS);

			// Layer control - defaults to topright
			L.control.layers(baseMaps).addTo(mapRef.current);

			// Zoom control
			L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);

			setMapContainer(ref);
		}
	}, [setMapContainer]);

	// When the popup actually renders, this is called and allows us to store the popup div.  We actually
	// store the inner containing element so we can later 'portal' to it.
	const popupWasOpened = useCallback((e: any) =>
	{
		setPopupRef(((e.popup._wrapper as HTMLDivElement)?.getElementsByClassName('mapPopupView').item(0) as HTMLDivElement) ?? undefined);
	}, [setPopupRef]);

	useEffect(() =>
	{
		if (orgFarmFieldList)
		{
			setFields(orgFarmFieldList.flatMap(o => o.FarmList.flatMap(fa => fa.Fields)));
		}
	}, [orgFarmFieldList]);

	// When our display geometry changes (or the state changes such that the display should too)
	// we remove all geojson data and recreate it.
	useEffect(() =>
	{
		geoJsonLayer.clearLayers();

		if (mapRef.current && fields?.length > 0)
		{
			// Get all of the boundaries
			// There are some cases where the Fields have no Boundary, this may only be during
			// early development
			const fieldBoundaries: Feature<Geometry, IJdConnectField>[] = [];
			for (let i = 0, len = fields.length; i < len; i++)
			{
				const field = fields[i];
				if (field.Geometry)
				{
					fieldBoundaries.push(feature(field.Geometry, field));
				}
			}

			let featureLayer: L.GeoJSON<any> = undefined;
			if (fieldBoundaries.length > 0)
			{
				featureLayer = geoJsonLayer.addData(featureCollection(fieldBoundaries)).bindTooltip(function (layer: L.Layer)
				{
					const name = get(layer, 'feature.properties.Name', '');
					const acres = Number(get(layer, 'feature.properties.Area', '')).toFixed(1);
					return name + '<br>' + 'Acres: ' + acres;
				}) as L.GeoJSON<any>;
				

				// Only zoom to fit all fields on the first draw, selecting a field should not zoom to all fields again
				if (currentSelectedFieldIds?.length === 0)
				{
					// Padding will scoot the map over a bit to give space for the overlay on the left
					mapRef.current.fitBounds(featureLayer.getBounds(), { paddingTopLeft: [0, 0] });
				}
				else
				{
					onCenterSelected();
				}

				// Hook into the user clicking on this feature and potentially bubble up the interaction
				featureLayer.on('click', (l: LeafletMouseEvent) =>
				{
					const clickedFeature = l.propagatedFrom?.feature as Feature<Geometry, IJdConnectField>;
					const fieldId = clickedFeature.properties.Id;
					selectSingleField(fieldId);
				});
			}

			geoJsonLayer.setStyle(styler);

			if (featureLayer)
			{
				return () =>
				{
					featureLayer.off('click');
				};
			}
		}
	}, [fields, mapRef.current, popupWasOpened, currentSelectedFieldIds]);

	useEffect(() =>
	{
		if (!mapContainer)
		{
			return;
		}

		// Ignore the geoman controls on all layers - for now
		geoJsonLayer.options = { pmIgnore: true };
		geoJsonLayer.addTo(mapRef.current);
		geoJsonLayer.setStyle(styler);

	}, [mapContainer, mapRef.current]);

	const onCenterAll = useCallback(() =>
	{
		// Padding will scoot the map over a bit to give space for the overlay on the left
		mapRef.current.fitBounds(geoJsonLayer.getBounds(), { paddingTopLeft: [0, 0] });
	}, [mapRef.current, geoJsonLayer]);

	const onCenterSelected = useCallback(() =>
	{
		if (currentSelectedFieldIds && currentSelectedFieldIds.length > 0)
		{
			// Get all of the boundaries
			// There are some cases where the Fields have no Boundary, this may only be during
			// early development
			const selectedFieldBoundaries: Feature<Geometry, IJdConnectField>[] = [];
			for (let i = 0, len = currentSelectedFieldIds.length; i < len; i++)
			{
				const field = fields.find(f => f.Id === currentSelectedFieldIds[i]);
				if (field && field.Geometry)
				{
					selectedFieldBoundaries.push(feature(field.Geometry, field));
				}
			}
			if (selectedFieldBoundaries.length > 0)
			{
				const collection = featureCollection(selectedFieldBoundaries);
				const group = L.geoJSON(collection);
				// Padding will scoot the map over a bit to give space for the overlay on the left
				mapRef.current.fitBounds(group.getBounds(), { paddingTopLeft: [0, 0] });

				geoJsonLayer.setStyle(styler);
			}
		}
	}, [currentSelectedFieldIds, fields, mapRef.current]);

	const toggleLocationSearch = useCallback(() =>
	{
		SetShowMapSearch(!DisplayMapSearch);
	}, [mapRef.current, DisplayMapSearch, SetShowMapSearch]);

	const geocoder = new MapboxGeocoder({
		accessToken: MapboxToken,
		types: 'address,region,place,postcode,locality,neighborhood',
		countries: 'us,ca',
		reverseGeocode: true
	});

	useEffect(() => 
	{
		// Get geocoder result to move the map.
		geocoder.on('result', (e: { result: MapboxGeocoder.Result; }) =>
		{
			const results = e.result;
			if (results.place_type[0] === 'address')
			{
				// Perform some acrobatics to convert the point into a fake marker and then get some bounds
				// so that we can add some padding on the left to scoot it over into the center of the right view!
				const resultPosition: LatLngExpression = [results.center[1], results.center[0]];
				const tempMarker: L.Marker = L.marker(resultPosition);
				const tempMarkerLatLngArr = [tempMarker.getLatLng()];
				const bounds = L.latLngBounds(tempMarkerLatLngArr);
				mapRef.current.fitBounds(bounds, { paddingTopLeft: [450, 0] });
			}
			else
			{
				// Anything not an address should land here! Just use the bbox to make some bounds
				mapRef.current.fitBounds([
					[results.bbox[3], results.bbox[0]],
					[results.bbox[1], results.bbox[2]]
				], { paddingTopLeft: [450, 0] });
			}
		});

	}, [geocoder, mapRef.current]);

	return (
		<div className={'JdConnectFieldMap'} style={{ width: '100%', height: '100%' }}>
			<div id='mapId' ref={installMap} style={{ width: '100%', height: '100%' }} />
			<div
				id='geocoder'
				style={{ position: 'absolute', top: 73, left: 'calc(100vw - 307px)', zIndex: 998, display: !DisplayMapSearch ? 'none' : 'inherit' }}
			/>
			<ZoomTray
				mapRef={mapRef}
				onCenterAll={onCenterAll}
				onCenterSelected={onCenterSelected}
				toggleLocationSearch={toggleLocationSearch}
				displayTools={true}
				geocoder={geocoder}
			/>
		</div>
	);
};

const mapStateToProps = (state: RootState) => ({
	DisplayMapSearch: state.ui.ShowMapSearch,
	MapboxToken: state.config.MapboxAccessToken,
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
{
	return {
		SetShowMapSearch: ((value: boolean) => dispatch(setShowMapSearch(value))),
	};
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const JdConnectFieldMap = connector(MapComponent);